import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { UploadCloud, FileSpreadsheet, ArrowRight, ArrowLeft, CheckCircle2, Undo2, PlayCircle, Users2, Send, UserPlus, Home, Link2, Sparkles, GitCompare, ChevronDown, ChevronUp, FileText, X, Mail, MailX, KeyRound, Trash2, AlertTriangle } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../components/ui/alert-dialog";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "");

export default function ImportHub() {
  const [step, setStep] = useState(1);
  // Iter122i · scroll a inizio pagina ad ogni step del wizard di import.
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, [step]);
  const [busy, setBusy] = useState(false);
  const [analisi, setAnalisi] = useState(null);
  // Iter122dq · Nuovo modello UI: gestionale + cosa → entita backend
  //   gestionale=danea + cosa=anagrafica → entita="danea" (RAC + soggetti + unità + account)
  //   gestionale=danea + cosa=condomini → entita="condomini" (import elenco stabili)
  //   gestionale=danea + cosa=fornitori → entita="fornitori"
  //   gestionale=manuale + cosa=anagrafica → entita="residenti"
  //   gestionale=manuale + cosa=condomini → entita="condomini"
  //   gestionale=manuale + cosa=fornitori → entita="fornitori"
  const [gestionale, setGestionale] = useState("danea");
  const [cosa, setCosa] = useState("anagrafica");
  const entita = (gestionale === "danea" && cosa === "anagrafica") ? "danea"
    : (cosa === "anagrafica" ? "residenti" : cosa);
  const [mapping, setMapping] = useState({});
  const [condominioId, setCondominioId] = useState("");
  // Iter122dl · sync new model è ora SEMPRE ON (default true, toggle nascosto).
  // La fase di migrazione dal legacy è conclusa: ogni nuovo import Danea deve popolare
  // direttamente il registro Unit-First (soggetti + unita_immobiliari + soggetti_unita),
  // altrimenti l'anagrafica risulta "non migrata" e vuota anche a fronte di account creati.
  const [syncNewModel] = useState(true);
  const [condomini, setCondomini] = useState([]);
  const [esito, setEsito] = useState(null);
  const [batches, setBatches] = useState([]);
  const fileRef = useRef(null);
  const [dedupCond, setDedupCond] = useState("");
  const [dedupPreview, setDedupPreview] = useState(null);
  const [dedupBusy, setDedupBusy] = useState(false);
  // Iter117 · Dialog Report lotto
  const [reportOpen, setReportOpen] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  // Iter122dn · Storico collapse (default chiuso) + warning annulla impatta batch successivi
  const [storicoOpen, setStoricoOpen] = useState(false);
  const [annullaTarget, setAnnullaTarget] = useState(null); // {bid, impacted_by:[...]}
  // Iter122do · Mappatura colonne → campi Ginius: collapsible chiuso di default, in fondo alla card step 2
  const [mappaturaOpen, setMappaturaOpen] = useState(false);
  // Iter120c · Preview normalizzazione ruoli (raw → normalizzato)
  const [ruoliMap, setRuoliMap] = useState({}); // { "pr.": "Proprietario", ... }
  const [ruoliSconosciuti, setRuoliSconosciuti] = useState([]);
  const [ruoliRiconosciuti, setRuoliRiconosciuti] = useState([]);
  // Iter120e · override manuale ruoli sconosciuti (raw → ruolo_standard scelto dall'utente)
  const [ruoliOverride, setRuoliOverride] = useState({});

  // Iter122fp · Import PDF Danea (integra dati Excel già presenti). Flusso alternativo:
  // 1) upload PDF → /import/danea-pdf/analizza → sessione + preview
  // 2) selezione condominio target → /import/danea-pdf/conferma → report additivo
  const isPdfMode = gestionale === "danea_pdf";
  const [pdfSession, setPdfSession] = useState(null);   // { session_id, header, summary, units_preview }
  const [pdfReport, setPdfReport] = useState(null);
  const [pdfBusy, setPdfBusy] = useState(false);
  const analizzaPdf = async (file) => {
    if (!file) return;
    setPdfBusy(true);
    setPdfReport(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/import/danea-pdf/analizza", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPdfSession(data);
      toast.success(`PDF Danea riconosciuto: ${data.summary.n_unita} unità e ${data.summary.n_soggetti} soggetti.`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "PDF non riconosciuto");
      setPdfSession(null);
    } finally { setPdfBusy(false); }
  };
  const confermaPdf = async () => {
    if (!pdfSession?.session_id || !condominioId) {
      toast.error("Seleziona il condominio di destinazione");
      return;
    }
    setPdfBusy(true);
    try {
      const { data } = await api.post("/import/danea-pdf/conferma", {
        session_id: pdfSession.session_id,
        condominio_id: condominioId,
      });
      setPdfReport(data);
      toast.success(`Integrazione completata su ${data.condominio}.`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Integrazione non riuscita");
    } finally { setPdfBusy(false); }
  };
  const resetPdf = () => { setPdfSession(null); setPdfReport(null); setCondominioId(""); };
  // Iter122ft · Sostituzione aggregato → individuale (post-import PDF Danea)
  const [aggrDialog, setAggrDialog] = useState(null); // { aggregato_id, aggregato_nome, aggregato_ha_account, unita_sub, unita_id, candidati:[] }
  const [aggrTarget, setAggrTarget] = useState("");
  const [aggrBusy, setAggrBusy] = useState(false);
  const openAggrDialog = (a) => {
    setAggrDialog(a);
    setAggrTarget(a.candidati?.[0]?.soggetto_id || "");
  };
  const sostituisciAggregato = async () => {
    if (!aggrDialog || !aggrTarget) return;
    setAggrBusy(true);
    try {
      const { data } = await api.post(`/soggetti/${aggrDialog.aggregato_id}/sostituisci`, {
        target_soggetto_id: aggrTarget,
        unita_id: aggrDialog.unita_id,
        migra_account: true,
        motivo: "Sostituzione post-import PDF Danea",
      });
      // Rimuovi l'aggregato dalla lista visualizzata
      setPdfReport((prev) => {
        if (!prev) return prev;
        const filtered = (prev.stats?.aggregati_sostituibili || []).filter((x) => x.aggregato_id !== aggrDialog.aggregato_id);
        return { ...prev, stats: { ...prev.stats, aggregati_sostituibili: filtered } };
      });
      const trs = data.transferred || {};
      const parts = [];
      if (trs.user_id) parts.push("account trasferito");
      if (trs.consensi) parts.push("consensi migrati");
      if (trs.documenti) parts.push("documenti collegati");
      if (trs.rac_log) parts.push("storico RAC preservato");
      toast.success(`"${data.aggregato.nome}" sostituito con "${data.destinatario.nome}"${parts.length ? ` — ${parts.join(", ")}` : ""}.`);
      setAggrDialog(null);
      setAggrTarget("");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Sostituzione non riuscita");
    } finally { setAggrBusy(false); }
  };

  const apriReport = async (bid) => {
    setReportOpen(true);
    setReportLoading(true);
    setReportData(null);
    try {
      const { data } = await api.get(`/import/batches/${bid}/report`);
      setReportData(data);
    } catch (e) {
      toast.error(formatApiError(e));
      setReportOpen(false);
    } finally { setReportLoading(false); }
  };

  const runDedup = async (dry) => {
    if (!dedupCond) { toast.error("Seleziona un condominio"); return; }
    setDedupBusy(true);
    try {
      const { data } = await api.post("/import/deduplica-residenti", { condominio_id: dedupCond, dry_run: dry });
      if (dry) {
        setDedupPreview(data);
        if (data.gruppi_duplicati === 0 && (data.proprieta_duplicate_previste || 0) === 0) toast.success("Nessun duplicato trovato in questo condominio");
      } else {
        toast.success(`Deduplica completata: ${data.gruppi_unificati} anagrafiche unificate, ${data.residenti_cestinati} duplicati nel cestino, ${data.proprieta_riassociate} schede ri-associate, ${data.proprieta_duplicate_unificate || 0} schede RAC duplicate unificate`);
        setDedupPreview(null);
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Deduplica non riuscita");
    } finally { setDedupBusy(false); }
  };

  const loadBatches = async () => {
    try { const { data } = await api.get("/import/batches"); setBatches(data); } catch {}
  };
  useEffect(() => {
    loadBatches();
    api.get("/condomini").then(({ data }) => setCondomini(Array.isArray(data) ? data : data.condomini || [])).catch(() => {});
  }, []);

  // Iter120c · Aggiorna la preview di normalizzazione ruoli ogni volta che l'utente
  // mappa una colonna al campo "ruolo" (o cambia il file/mapping). Chiama backend
  // per garantire coerenza esatta con `_normalize_ruolo` server-side.
  useEffect(() => {
    if (!analisi) { setRuoliMap({}); setRuoliSconosciuti([]); return; }
    const ruoloCol = Object.keys(mapping).find((c) => mapping[c] === "ruolo");
    if (!ruoloCol) { setRuoliMap({}); setRuoliSconosciuti([]); return; }
    // Estrai valori unici dal campione (fino a 50 righe) per la preview
    const rows = analisi.campione || [];
    const valoriUniq = Array.from(new Set(
      rows.map((r) => (r[ruoloCol] || "").toString().trim()).filter(Boolean)
    ));
    if (valoriUniq.length === 0) { setRuoliMap({}); setRuoliSconosciuti([]); return; }
    api.post("/import/normalizza-ruoli", { valori: valoriUniq })
      .then(({ data }) => {
        setRuoliMap(data.mapping || {});
        setRuoliSconosciuti(data.sconosciuti || []);
        setRuoliRiconosciuti(data.ruoli_riconosciuti || []);
      })
      .catch(() => { setRuoliMap({}); setRuoliSconosciuti([]); setRuoliRiconosciuti([]); });
  }, [analisi, mapping]);

  // Reset degli override quando cambia il file analizzato
  useEffect(() => { setRuoliOverride({}); }, [analisi?.session_id]);

  const analizza = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/import/analizza", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setAnalisi(data);
      // Iter122dq · Adatta gestionale + cosa in base al connettore rilevato dall'analisi backend
      const ent = data.entita_suggerita || entita;
      const suggested = {
        danea: { gest: "danea", cosa: "anagrafica" },
        residenti: { gest: "manuale", cosa: "anagrafica" },
        condomini: { gest: data.connettore === "danea_condomini" ? "danea" : "manuale", cosa: "condomini" },
        fornitori: { gest: data.connettore === "danea_fornitori" ? "danea" : "manuale", cosa: "fornitori" },
      }[ent];
      if (suggested) { setGestionale(suggested.gest); setCosa(suggested.cosa); }
      setMapping(data.mapping_proposto[ent] || {});
      if (data.connettore) toast.success(`Riconosciuto export Danea Domus Studio (${{ danea: "anagrafica condòmini", danea_condomini: "condomìni", danea_fornitori: "fornitori" }[data.connettore]}): mappatura applicata automaticamente`);
      setEsito(null);
      setStep(2);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Lettura del file non riuscita"); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const run = async (commit) => {
    setBusy(true);
    try {
      const overrides = Object.keys(ruoliOverride).length > 0 ? ruoliOverride : null;
      const { data } = await api.post(`/import/${analisi.session_id}/${commit ? "esegui" : "dry-run"}`,
        { entita, mapping, condominio_id: condominioId || null, sync_new_model: syncNewModel, ruoli_override: overrides });
      setEsito(data);
      if (commit) { toast.success("Import completato"); loadBatches(); setStep(3); }
      else setStep(3);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Operazione non riuscita"); }
    finally { setBusy(false); }
  };

  const annulla = async (bid, force = false) => {
    if (!force) {
      // Iter122dn · preflight per rilevare batch successivi impattati
      try {
        const { data } = await api.get(`/import/batches/${bid}/annulla-preflight`);
        if (data.impacted_by && data.impacted_by.length > 0) {
          setAnnullaTarget({ bid, impacted_by: data.impacted_by });
          return;
        }
      } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail) || "Verifica preflight non riuscita");
        return;
      }
      if (!window.confirm("Annullare questo lotto di import? I record creati saranno rimossi e le modifiche ripristinate.")) return;
    }
    try {
      const { data } = await api.post(`/import/batches/${bid}/annulla${force ? "?force=true" : ""}`);
      toast.success(`Lotto annullato: ${data.rimossi} rimossi, ${data.ripristinati} ripristinati`);
      setAnnullaTarget(null);
      loadBatches();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Annullamento non riuscito"); }
  };

  const sollecitaNuovi = async (bid) => {
    if (!bid) return;
    if (!window.confirm("Inviare la richiesta RAC a tutti i soggetti creati con questo import?")) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/import/batches/${bid}/sollecita-nuovi`);
      toast.success(`RAC inviate: ${data.inviati} · senza email: ${data.senza_email} · errori: ${data.errori}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Invio non riuscito");
    } finally { setBusy(false); }
  };

  // Iter122dm · elimina definitivamente un lotto annullato dallo storico
  const [deleteTarget, setDeleteTarget] = useState(null); // batch object
  const [deleting, setDeleting] = useState(false);
  const eliminaDefinitivamente = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.delete(`/import/batches/${deleteTarget.id}`);
      toast.success("Lotto eliminato definitivamente dallo storico");
      setDeleteTarget(null);
      loadBatches();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Eliminazione non riuscita");
    } finally { setDeleting(false); }
  };

  const campiEntita = analisi?.entita?.[entita]?.campi || [];

  return (
    <div className="space-y-6" data-testid="import-hub-page">
      <div>
        <h1 className="font-display text-4xl sm:text-5xl font-bold text-slate-800">Importa dati</h1>
        <p className="text-base text-slate-600 mt-1">Carica l'export del tuo gestionale (Excel/CSV) senza modificarlo: il sistema riconosce le colonne, accorpa le proprietà catastali per soggetto e ti mostra l'anteprima prima di importare.</p>
      </div>

      {step === 1 && (
        <Card className="p-6 border-2">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-5">
            <div>
              <Label>Modello importazione dati<span className="text-red-500 ml-0.5">*</span></Label>
              <p className="text-[10px] leading-tight text-slate-500 mt-0.5 mb-1">Seleziona il gestionale fonte dei dati.</p>
              <select data-testid="import-gestionale" value={gestionale}
                onChange={(e) => { setGestionale(e.target.value); resetPdf(); setAnalisi(null); setEsito(null); }}
                className="mt-1 h-12 w-full rounded-lg border-2 border-slate-300 px-3 text-base bg-white">
                <option value="danea">Danea Domus Studio · Excel</option>
                <option value="danea_pdf">Danea Domus Studio · PDF Registro Anagrafe (integra CF e conduttori)</option>
                <option value="manuale">Nessun gestionale (file Excel di import)</option>
              </select>
              <p className="text-[10px] leading-tight text-slate-500 mt-1">
                * Se il tuo gestionale non è nell'elenco, contatta il team di Ginius.
              </p>
              <p className="text-[10px] leading-tight text-slate-500 mt-1">
                ** In alternativa puoi scaricare il modello di file di import dalle sezioni <b>Anagrafica Condòmini</b> e <b>Stabili</b>, popolarlo con i tuoi dati e importarlo qui (File Excel di import). Oppure puoi aggiungere/modificare condomìni, condòmini e fornitori singolarmente nelle rispettive sezioni compilando manualmente i campi.
              </p>
              {isPdfMode && (
                <p className="text-[11px] leading-tight text-brand mt-2 font-semibold">
                  ★ Modalità PDF · integrazione additiva: aggiungerà CF, comproprietari, conduttori e "altri diritti" ai soggetti già presenti — <b>senza mai sovrascrivere</b> dati già popolati.
                </p>
              )}
            </div>
            {!isPdfMode && (
            <div>
              <Label>Cosa vuoi importare?</Label>
              <select data-testid="import-entita" value={cosa}
                onChange={(e) => setCosa(e.target.value)}
                className="mt-1 h-12 w-full rounded-lg border-2 border-slate-300 px-3 text-base bg-white">
                <option value="condomini">Condomìni</option>
                <option value="anagrafica">Anagrafica Condòmini</option>
                <option value="fornitori">Fornitori</option>
              </select>
              <p className="text-[10px] leading-tight text-slate-500 mt-1">
                Le opzioni sono parametrizzate sul modello dati del gestionale scelto ({gestionale === "danea" ? "Danea Domus Studio" : "file Excel di import"}).
              </p>
            </div>
            )}
            {(entita === "residenti" || entita === "danea" || isPdfMode) && (
              <div>
                <Label>Condominio di destinazione *</Label>
                <select data-testid="import-condominio" value={condominioId}
                  onChange={(e) => setCondominioId(e.target.value)}
                  className="mt-1 h-12 w-full rounded-lg border-2 border-slate-300 px-3 text-base bg-white">
                  <option value="">— Seleziona —</option>
                  {condomini.map((c) => <option key={c.id} value={c.id}>{c.nome}</option>)}
                </select>
                {isPdfMode && <p className="text-[10px] leading-tight text-slate-500 mt-1">Il PDF verrà integrato in questo condominio già popolato.</p>}
              </div>
            )}
          </div>
          {/* Iter122fp · dropzone: PDF Danea (integrazione additiva) o Excel/CSV (import standard) */}
          {isPdfMode ? (
            <>
              {!pdfSession && (
                <div
                  data-testid="import-pdf-dropzone"
                  role="button"
                  tabIndex={0}
                  onClick={() => { if (!pdfBusy && fileRef.current) fileRef.current.click(); }}
                  onKeyDown={(e) => { if ((e.key === "Enter" || e.key === " ") && !pdfBusy) { e.preventDefault(); fileRef.current?.click(); } }}
                  onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); e.currentTarget.classList.add("bg-brand/10"); }}
                  onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); e.currentTarget.classList.remove("bg-brand/10"); }}
                  onDrop={(e) => { e.preventDefault(); e.stopPropagation(); e.currentTarget.classList.remove("bg-brand/10"); const f = e.dataTransfer?.files?.[0]; if (f && !pdfBusy) analizzaPdf(f); }}
                  className="flex flex-col items-center justify-center gap-3 border-2 border-dashed border-brand/40 rounded-2xl p-10 cursor-pointer hover:bg-brand/5 transition-colors"
                >
                  <FileText className="w-12 h-12 text-brand" />
                  <span className="font-bold text-lg text-slate-700">{pdfBusy ? "Analisi PDF in corso..." : "Carica il PDF Danea 'Registro Anagrafe Condominiale'"}</span>
                  <span className="text-sm text-slate-500">.pdf — max 15 MB — clicca oppure trascina qui il file</span>
                </div>
              )}
              {pdfSession && !pdfReport && (
                <div className="space-y-4 mt-4" data-testid="import-pdf-preview">
                  <div className="p-4 rounded-lg bg-brand/5 border-2 border-brand/20">
                    <div className="font-bold text-slate-800">{pdfSession.filename}</div>
                    <div className="text-sm text-slate-600 mt-1">
                      Trovate <b>{pdfSession.summary.n_unita}</b> unità immobiliari · <b>{pdfSession.summary.n_soggetti}</b> soggetti totali
                    </div>
                    <div className="flex flex-wrap gap-3 mt-2 text-xs">
                      {Object.entries(pdfSession.summary.by_role || {}).map(([k, v]) => (
                        <span key={k} className="px-2 py-1 rounded bg-white border border-slate-200">
                          <b>{v}</b> {k.replace("_", " ")}
                        </span>
                      ))}
                    </div>
                    {pdfSession.header?.nome && <div className="text-xs text-slate-500 mt-2">Header PDF: {pdfSession.header.nome} {pdfSession.header.codice_fiscale ? ` · CF ${pdfSession.header.codice_fiscale}` : ""}</div>}
                  </div>
                  {(pdfSession.units_preview || []).length > 0 && (
                    <div className="border-2 border-slate-200 rounded-lg overflow-hidden">
                      <div className="bg-slate-50 px-3 py-2 font-semibold text-slate-700 text-sm">Anteprima prime {pdfSession.units_preview.length} unità</div>
                      <div className="max-h-72 overflow-y-auto divide-y divide-slate-100">
                        {pdfSession.units_preview.map((u, i) => (
                          <div key={i} className="p-3 text-sm">
                            <div className="font-semibold text-slate-800">Sub {u.sub} · Fg. {u.foglio} · Part. {u.particella}</div>
                            <ul className="mt-1 space-y-0.5 text-xs text-slate-600">
                              {(u.soggetti_preview || []).map((s, si) => (
                                <li key={si}>
                                  <span className="inline-block px-1.5 py-0.5 rounded bg-slate-100 text-slate-700 mr-2 text-[10px] uppercase font-bold">{s.ruolo}</span>
                                  {s.nome} {s.cf ? `— ${s.cf}` : <em className="text-amber-700">CF assente</em>}
                                </li>
                              ))}
                            </ul>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  <div className="flex flex-wrap gap-3">
                    <Button data-testid="import-pdf-conferma" className="bg-brand hover:bg-brand-dark" onClick={confermaPdf} disabled={pdfBusy || !condominioId}>
                      <CheckCircle2 className="w-4 h-4 mr-2" /> {pdfBusy ? "Integrazione..." : "Conferma integrazione additiva"}
                    </Button>
                    <Button variant="outline" onClick={resetPdf} disabled={pdfBusy}>
                      <Undo2 className="w-4 h-4 mr-2" /> Ricomincia
                    </Button>
                  </div>
                  {!condominioId && <p className="text-xs text-red-700">Seleziona il condominio di destinazione qui sopra prima di confermare.</p>}
                </div>
              )}
              {pdfReport && (
                <div className="space-y-3 mt-4" data-testid="import-pdf-report">
                  <div className="p-4 rounded-lg bg-green-50 border-2 border-green-200">
                    <div className="font-bold text-green-800 flex items-center gap-2"><CheckCircle2 className="w-5 h-5" /> Integrazione completata su {pdfReport.condominio}</div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-3 text-sm">
                      <div className="bg-white rounded p-2 border border-green-200"><b>{pdfReport.stats.unita_create}</b><br/><span className="text-xs text-slate-600">Unità create</span></div>
                      <div className="bg-white rounded p-2 border border-green-200"><b>{pdfReport.stats.unita_aggiornate}</b><br/><span className="text-xs text-slate-600">Unità aggiornate</span></div>
                      <div className="bg-white rounded p-2 border border-green-200"><b>{pdfReport.stats.soggetti_creati}</b><br/><span className="text-xs text-slate-600">Soggetti creati</span></div>
                      <div className="bg-white rounded p-2 border border-green-200"><b>{pdfReport.stats.soggetti_aggiornati}</b><br/><span className="text-xs text-slate-600">Soggetti aggiornati</span></div>
                      <div className="bg-white rounded p-2 border border-green-200"><b>{pdfReport.stats.relazioni_create}</b><br/><span className="text-xs text-slate-600">Relazioni create</span></div>
                      <div className="bg-white rounded p-2 border border-green-200"><b>{pdfReport.stats.relazioni_aggiornate}</b><br/><span className="text-xs text-slate-600">Relazioni aggiornate</span></div>
                    </div>
                  </div>
                  {(pdfReport.stats.conflitti_cf || []).length > 0 && (
                    <div className="p-4 rounded-lg bg-amber-50 border-2 border-amber-200" data-testid="import-pdf-conflitti">
                      <div className="font-bold text-amber-800 flex items-center gap-2 mb-2">
                        ⚠ Conflitti CF risolti ({pdfReport.stats.conflitti_cf.length})
                      </div>
                      <div className="text-xs text-amber-900 mb-2">
                        Il CF di questi soggetti PDF combaciava con un soggetto Excel aggregato (es. "Cognome1 Cognome2"). Ho creato comunque un nuovo soggetto individuale e lo trovi elencato sotto — potrai poi <b>sostituire l'aggregato</b> dalla scheda Unità.
                      </div>
                      <ul className="space-y-1 text-xs text-amber-900">
                        {pdfReport.stats.conflitti_cf.map((c, i) => (
                          <li key={i}><b>Sub {c.sub}:</b> {c.nota}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {(pdfReport.stats.aggregati_sostituibili || []).length > 0 && (
                    <div className="p-4 rounded-lg bg-indigo-50 border-2 border-indigo-200" data-testid="import-pdf-aggregati">
                      <div className="font-bold text-indigo-900 flex items-center gap-2 mb-2">
                        🔀 Aggregati sostituibili ({pdfReport.stats.aggregati_sostituibili.length})
                      </div>
                      <div className="text-xs text-indigo-900 mb-2">
                        Sull'unità sono stati creati soggetti individuali dal PDF. L'aggregato Excel può essere sostituito
                        <b> trasferendo l'account e i consensi al soggetto individuale scelto</b> (l'aggregato viene solo archiviato: ripristino possibile).
                      </div>
                      <ul className="space-y-2">
                        {pdfReport.stats.aggregati_sostituibili.map((a, i) => (
                          <li key={i} className="bg-white rounded p-2 border border-indigo-200 flex flex-wrap items-center justify-between gap-2">
                            <div className="text-sm min-w-0 flex-1">
                              <div className="font-semibold text-slate-800 truncate">
                                Sub {a.unita_sub} · <span className="text-indigo-900">{a.aggregato_nome}</span>
                                {a.aggregato_ha_account && <span className="ml-2 text-[10px] uppercase font-bold px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-300">Con account</span>}
                              </div>
                              <div className="text-xs text-slate-500">
                                Candidati individuali: {(a.candidati || []).map((c) => c.nome).join(" · ")}
                              </div>
                            </div>
                            <Button size="sm" data-testid={`sostituisci-aggregato-${a.aggregato_id}`}
                              className="h-8 bg-indigo-600 hover:bg-indigo-700 text-white"
                              onClick={() => openAggrDialog(a)}>
                              Sostituisci
                            </Button>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  <Button variant="outline" onClick={resetPdf}>
                    <Undo2 className="w-4 h-4 mr-2" /> Nuovo import PDF
                  </Button>
                </div>
              )}
              {aggrDialog && (
                <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-3" onClick={() => !aggrBusy && setAggrDialog(null)} data-testid="aggr-overlay">
                  <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[85vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()} data-testid="aggr-dialog">
                    <div className="px-4 py-3 border-b border-slate-200">
                      <div className="font-display font-bold text-lg text-slate-800">Sostituzione soggetto aggregato</div>
                      <div className="text-xs text-slate-500 mt-0.5">Sub {aggrDialog.unita_sub} · Aggregato: <b>{aggrDialog.aggregato_nome}</b></div>
                    </div>
                    <div className="flex-1 overflow-y-auto p-4 space-y-4">
                      <div>
                        <div className="text-sm font-semibold text-slate-700 mb-2">Trasferisci l'account a:</div>
                        <ul className="space-y-2">
                          {(aggrDialog.candidati || []).map((c) => (
                            <li key={c.soggetto_id}>
                              <label className={`flex items-start gap-2 p-3 rounded-lg border-2 cursor-pointer ${aggrTarget === c.soggetto_id ? "border-brand bg-[#FBEEF3]/50" : "border-slate-200 hover:border-slate-300"}`}>
                                <input type="radio" name="aggr-target" className="mt-1" value={c.soggetto_id}
                                  data-testid={`aggr-target-${c.soggetto_id}`}
                                  checked={aggrTarget === c.soggetto_id}
                                  onChange={() => setAggrTarget(c.soggetto_id)} />
                                <div className="min-w-0 flex-1">
                                  <div className="font-semibold text-slate-800">{c.nome}</div>
                                  {c.codice_fiscale && <div className="text-xs text-slate-500 font-mono">{c.codice_fiscale}</div>}
                                </div>
                              </label>
                            </li>
                          ))}
                        </ul>
                      </div>
                      {aggrDialog.aggregato_ha_account && (
                        <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-900">
                          <b>Verranno trasferiti:</b> account utente, consensi privacy, documenti, storico RAC.<br/>
                          L'aggregato "<b>{aggrDialog.aggregato_nome}</b>" verrà solo <b>archiviato</b> (nessuna cancellazione fisica; ripristino sempre possibile).
                        </div>
                      )}
                      {!aggrDialog.aggregato_ha_account && (
                        <div className="p-3 rounded-lg bg-slate-50 border border-slate-200 text-xs text-slate-700">
                          Questo aggregato non ha un account collegato: verranno comunque trasferiti eventuali consensi e documenti.
                        </div>
                      )}
                    </div>
                    <div className="px-4 py-3 border-t border-slate-200 flex items-center justify-end gap-2">
                      <Button variant="outline" size="sm" className="h-9" onClick={() => setAggrDialog(null)} disabled={aggrBusy}>Annulla</Button>
                      <Button data-testid="aggr-save" size="sm" className="h-9 bg-indigo-600 hover:bg-indigo-700 text-white"
                        onClick={sostituisciAggregato} disabled={aggrBusy || !aggrTarget}>
                        {aggrBusy ? "Sostituzione..." : "Conferma sostituzione"}
                      </Button>
                    </div>
                  </div>
                </div>
              )}
              <input ref={fileRef} type="file"
                accept=".pdf,application/pdf"
                className="hidden" disabled={pdfBusy}
                data-testid="import-pdf-file-input"
                onChange={(e) => { analizzaPdf(e.target.files?.[0]); if (e.target) e.target.value = ""; }} />
            </>
          ) : (
          <>
          <div
            data-testid="import-dropzone"
            role="button"
            tabIndex={0}
            onClick={() => { if (!busy && fileRef.current) fileRef.current.click(); }}
            onKeyDown={(e) => { if ((e.key === "Enter" || e.key === " ") && !busy) { e.preventDefault(); fileRef.current?.click(); } }}
            onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); e.currentTarget.classList.add("bg-brand/10"); }}
            onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); e.currentTarget.classList.remove("bg-brand/10"); }}
            onDrop={(e) => {
              e.preventDefault(); e.stopPropagation();
              e.currentTarget.classList.remove("bg-brand/10");
              const f = e.dataTransfer?.files?.[0];
              if (f && !busy) analizza(f);
            }}
            className="flex flex-col items-center justify-center gap-3 border-2 border-dashed border-brand/40 rounded-2xl p-10 cursor-pointer hover:bg-brand/5 transition-colors"
          >
            <UploadCloud className="w-12 h-12 text-brand" />
            <span className="font-bold text-lg text-slate-700">{busy ? "Analisi in corso..." : "Carica il file esportato dal gestionale"}</span>
            <span className="text-sm text-slate-500">.xlsx · .xls · .csv — max 15 MB — clicca oppure trascina qui il file</span>
          </div>
          <input ref={fileRef} type="file"
            accept=".xlsx,.xlsm,.xls,.csv,.txt,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,application/vnd.ms-excel.sheet.macroEnabled.12,text/csv,text/plain,application/octet-stream"
            className="hidden" disabled={busy}
            data-testid="import-file-input"
            onChange={(e) => { analizza(e.target.files?.[0]); }} />
          </>
          )}
        </Card>
      )}

      {step === 2 && analisi && (
        <Card className="p-6 border-2 space-y-5">
          <div className="flex items-center gap-2 text-slate-700">
            <FileSpreadsheet className="w-6 h-6 text-brand" />
            <b>{analisi.filename}</b> · {analisi.num_righe} righe
          </div>
          {analisi.connettore && (
            <div data-testid="danea-banner" className="rounded-lg border-2 border-emerald-300 bg-emerald-50 px-4 py-3 text-sm text-emerald-900">
              {analisi.connettore === "danea" && (
                <><b>Export Danea Domus Studio riconosciuto (anagrafica condòmini).</b> Verranno creati/aggiornati i condòmini con i dati catastali accorpati per soggetto,
                le schede anagrafiche (RAC) per ogni unità e — dove <b>crea_account</b> = SI — gli accessi all'app.</>
              )}
              {analisi.connettore === "danea_condomini" && (
                <><b>Export Danea Domus Studio riconosciuto (elenco condomìni).</b> Verranno creati/aggiornati gli stabili con codice fiscale, indirizzo, dati catastali e coordinate bancarie.</>
              )}
              {analisi.connettore === "danea_fornitori" && (
                <><b>Export Danea Domus Studio riconosciuto (fornitori).</b> Verranno creati/aggiornati i fornitori e il campo <b>Settore</b> viene convertito automaticamente
                nelle categorie Ginius (es. "idraulico" → Idraulica) per i suggerimenti negli affidi.</>
              )}
            </div>
          )}
          {(entita === "residenti" || entita === "danea") && (
            <div className="max-w-sm">
              <Label>Condominio di destinazione *</Label>
              <select data-testid="import-condominio-step2" value={condominioId}
                onChange={(e) => setCondominioId(e.target.value)}
                className="mt-1 h-11 w-full rounded-lg border-2 border-slate-300 px-3 text-base bg-white">
                <option value="">— Seleziona —</option>
                {condomini.map((c) => <option key={c.id} value={c.id}>{c.nome}</option>)}
              </select>
            </div>
          )}
          {entita === "danea" && (
            <div className="border-2 border-emerald-200 bg-emerald-50 rounded-lg p-3 max-w-2xl text-sm text-emerald-900" data-testid="import-sync-new-model">
              I nuovi condòmini vengono creati automaticamente nel <b>Registro Anagrafico</b> (unità immobiliari + soggetti) — nessuna configurazione richiesta.
            </div>
          )}
          <div>
            <h3 className="font-display text-lg font-bold mb-2">Anteprima file (prime {analisi.campione.length} righe)</h3>
            {(() => {
              const ruoloCol = Object.keys(mapping).find((c) => mapping[c] === "ruolo");
              const nRuoli = Object.keys(ruoliMap).length;
              if (!ruoloCol || nRuoli === 0) return null;
              // Iter120e · un raw è "sconosciuto" se lo era originariamente E non ha ancora un override.
              const isUnknown = (raw) => ruoliSconosciuti.indexOf(raw) !== -1 && !ruoliOverride[raw];
              const nSconosciutiResidui = Object.keys(ruoliMap).filter(isUnknown).length;
              return (
                <div className="mb-2 rounded-lg border-2 border-emerald-200 bg-emerald-50 p-3 text-sm" data-testid="ruoli-preview-banner">
                  <div className="font-bold text-emerald-900 flex items-center gap-1">
                    <CheckCircle2 className="w-4 h-4" /> Normalizzazione ruolo (colonna &quot;{ruoloCol}&quot;)
                  </div>
                  <div className="mt-1 flex flex-wrap gap-1.5 items-center">
                    {Object.entries(ruoliMap).map(([raw, norm]) => {
                      const overridden = !!ruoliOverride[raw];
                      const displayed = overridden ? ruoliOverride[raw] : norm;
                      const unknown = isUnknown(raw);
                      return (
                        <span key={raw} data-testid={`ruolo-map-${raw}`}
                          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded border text-xs font-semibold ${unknown ? "bg-amber-50 border-amber-400 text-amber-800" : overridden ? "bg-sky-50 border-sky-400 text-sky-800" : "bg-white border-emerald-300 text-emerald-800"}`}
                          title={unknown ? "Non nell'elenco standard: seleziona un ruolo per mapparlo" : overridden ? "Override manuale attivo per questo import" : "Ruolo riconosciuto"}>
                          <span className="font-mono">&quot;{raw}&quot;</span>
                          <ArrowRight className="w-3 h-3" />
                          {unknown ? (
                            <select data-testid={`ruolo-override-${raw}`}
                              value={ruoliOverride[raw] || ""}
                              onChange={(e) => {
                                const val = e.target.value;
                                setRuoliOverride((prev) => {
                                  const n = { ...prev };
                                  if (!val) delete n[raw]; else n[raw] = val;
                                  return n;
                                });
                              }}
                              className="h-6 text-xs bg-white border border-amber-500 rounded px-1">
                              <option value="">— scegli ruolo —</option>
                              {ruoliRiconosciuti.map((r) => <option key={r} value={r}>{r}</option>)}
                            </select>
                          ) : (
                            <span>{displayed}</span>
                          )}
                          {overridden ? (
                            <button data-testid={`ruolo-override-clear-${raw}`}
                              className="text-sky-700 hover:text-red-600 font-bold text-xs"
                              onClick={() => setRuoliOverride((prev) => { const n = { ...prev }; delete n[raw]; return n; })}
                              title="Rimuovi override">✕</button>
                          ) : unknown ? <span>⚠</span> : <span>✓</span>}
                        </span>
                      );
                    })}
                  </div>
                  {nSconosciutiResidui > 0 && (
                    <div className="mt-1 text-xs text-amber-800">
                      ⚠ {nSconosciutiResidui} valore/i non standard: seleziona un ruolo dal menu accanto per mapparli manualmente,
                      oppure lascia così e verranno salvati letteralmente.
                    </div>
                  )}
                  {Object.keys(ruoliOverride).length > 0 && (
                    <div className="mt-1 text-xs text-sky-800" data-testid="ruoli-override-summary">
                      {Object.keys(ruoliOverride).length} override manuale/i attivo/i per questo import.
                    </div>
                  )}
                </div>
              );
            })()}
            <div className="overflow-x-auto border rounded-lg">
              <table className="text-xs w-full">
                <thead><tr className="bg-slate-100">{analisi.columns.map((c) => <th key={c} className="px-2 py-1.5 text-left font-bold whitespace-nowrap">{c}{mapping[c] === "ruolo" && <span className="ml-1 text-emerald-700">→ ruolo</span>}</th>)}</tr></thead>
                <tbody>
                  {analisi.campione.map((r, i) => (
                    <tr key={i} className="border-t">{analisi.columns.map((c) => {
                      const val = r[c];
                      const isRuolo = mapping[c] === "ruolo";
                      const raw = val != null ? String(val).trim() : "";
                      const overridden = isRuolo && raw ? ruoliOverride[raw] : null;
                      const norm = isRuolo && raw ? (overridden || ruoliMap[raw]) : null;
                      return (
                        <td key={c} className="px-2 py-1 whitespace-nowrap max-w-[220px] truncate">
                          {val}
                          {isRuolo && norm && (
                            <span className={`ml-1 inline-flex items-center gap-0.5 font-semibold ${overridden ? "text-sky-700" : "text-emerald-700"}`}>
                              <ArrowRight className="w-3 h-3" /> {norm}{overridden && <span title="Override manuale">*</span>}
                            </span>
                          )}
                        </td>
                      );
                    })}</tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <div className="rounded-lg border-2 border-slate-200 bg-slate-50/50" data-testid="mappatura-block">
            <button type="button" data-testid="mappatura-toggle"
              className="w-full flex items-center justify-between gap-3 text-left p-3"
              onClick={() => setMappaturaOpen((v) => !v)}>
              <div>
                <h3 className="font-display text-lg font-bold">Mappatura colonne → campi Ginius</h3>
                <p className="text-xs text-slate-500 mt-0.5">
                  {mappaturaOpen ? "Chiudi per nascondere l'associazione colonne del file → campi Ginius." : "Apri solo se vuoi personalizzare le associazioni proposte automaticamente."}
                </p>
              </div>
              {mappaturaOpen ? <ChevronUp className="w-5 h-5 text-slate-500 shrink-0" /> : <ChevronDown className="w-5 h-5 text-slate-500 shrink-0" />}
            </button>
            {mappaturaOpen && (
              <div className="px-3 pb-3 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {analisi.columns.map((col) => (
                  <div key={col} className="border rounded-lg p-2.5 bg-white">
                    <div className="text-xs font-bold text-slate-500 truncate" title={col}>{col}</div>
                    <select data-testid={`map-${col}`} value={mapping[col] || ""}
                      onChange={(e) => setMapping({ ...mapping, [col]: e.target.value })}
                      className="mt-1 h-9 w-full rounded border border-slate-300 px-2 text-sm bg-white">
                      <option value="">— ignora —</option>
                      {campiEntita.map((f) => <option key={f} value={f}>{f}</option>)}
                    </select>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="actions-row">
            <Button variant="outline" className="h-11" onClick={() => setStep(1)}><ArrowLeft className="w-4 h-4 mr-1" /> Indietro</Button>
            <Button data-testid="import-dryrun-btn" disabled={busy} onClick={() => run(false)}
              className="h-11 bg-brand hover:bg-brand-dark">
              {busy ? "Verifica..." : <>Verifica (anteprima import) <ArrowRight className="w-4 h-4 ml-1" /></>}
            </Button>
          </div>
        </Card>
      )}

      {step === 3 && esito && (
        <Card className="p-6 border-2 space-y-4" data-testid="import-esito-card">
          <h3 className="font-display text-2xl font-bold flex items-center gap-2">
            {esito.dry_run ? <>Anteprima import (nessun dato salvato)</> : <><CheckCircle2 className="w-7 h-7 text-green-600" /> Import completato</>}
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-center">
            {[["Da creare", esito.creati, "text-green-700"], ["Da aggiornare", esito.aggiornati, "text-blue-700"],
              ["Invariati", esito.invariati, "text-slate-500"], ["Scartate", esito.num_scartate, "text-red-700"]].map(([l, n, c]) => (
              <div key={l} className="rounded-lg border-2 py-3">
                <div className={`text-3xl font-bold ${c}`}>{n}</div>
                <div className="text-xs uppercase text-slate-500 font-bold">{esito.dry_run ? l : l.replace("Da creare", "Creati").replace("Da aggiornare", "Aggiornati")}</div>
              </div>
            ))}
          </div>
          {esito.entita === "danea" && (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-center">
              {[["Schede RAC da creare", esito.schede_create, "text-green-700"],
                ["Schede RAC da aggiornare", esito.schede_aggiornate, "text-blue-700"],
                [esito.dry_run ? "Account da creare" : "Account creati", esito.dry_run ? esito.account_da_creare : esito.account_creati, "text-brand"]].map(([l, n, c]) => (
                <div key={l} className="rounded-lg border-2 py-3 bg-slate-50">
                  <div className={`text-2xl font-bold ${c}`}>{n ?? 0}</div>
                  <div className="text-xs uppercase text-slate-500 font-bold">{esito.dry_run ? l : l.replace("da creare", "create").replace("da aggiornare", "aggiornate")}</div>
                </div>
              ))}
            </div>
          )}
          {esito.dry_run && esito.crea_default?.length > 0 && (
            <div data-testid="crea-default-alert" className="rounded-lg border-2 border-amber-400 bg-amber-50 px-4 py-3 text-sm text-amber-900">
              <b>⚠ Conferma richiesta:</b> per <b>{esito.crea_default.length}</b> soggett{esito.crea_default.length === 1 ? "o" : "i"} la colonna
              <b> crea_account</b> era vuota: verrà impostata su <b>SI</b> (creazione dell'accesso all'app, se c'è l'email).
              Se non vuoi creare l'accesso, torna alla mappatura e compila la colonna con NO nel file.
              <div className="mt-1 text-xs">{esito.crea_default.join(" · ")}</div>
            </div>
          )}
          {/* --- Iter95: Diff-report pre-import (dry-run) --- */}
          {esito.dry_run && esito.diff_report && (
            <DiffReportPanel dr={esito.diff_report} />
          )}
          {/* --- Report post-Import (Fase 4b+ Nuovo Modello) --- */}
          {!esito.dry_run && esito.sync_new_model && !esito.sync_new_model.error && (
            <PostImportReport esito={esito} onSollecita={() => sollecitaNuovi(esito.batch_id)} busy={busy} />
          )}
          {!esito.dry_run && esito.sync_new_model?.error && (
            <div className="rounded-lg border-2 border-red-300 bg-red-50 px-4 py-3 text-sm text-red-900" data-testid="post-import-sync-error">
              ⚠ Sincronizzazione sul Registro Anagrafico non riuscita: <i>{esito.sync_new_model.error}</i>. I dati sono comunque presenti sul modello Legacy.
            </div>
          )}
          {esito.senza_email?.length > 0 && (
            <div className="rounded-lg border border-slate-300 bg-slate-50 px-4 py-2.5 text-xs text-slate-600">
              <b>Nessun account possibile (manca l'email):</b> {esito.senza_email.join(" · ")}
            </div>
          )}
          {esito.dettagli?.length > 0 && (
            <div className="max-h-64 overflow-y-auto border rounded-lg divide-y">
              {esito.dettagli.map((d, i) => (
                <div key={i} className="px-3 py-2 text-sm flex flex-wrap items-center gap-2">
                  <span className={`px-1.5 py-0.5 rounded text-[11px] font-bold ${d.azione === "creato" ? "bg-green-100 text-green-800" : "bg-blue-100 text-blue-800"}`}>{d.azione}</span>
                  <b>{d.nome}</b>
                  {d.righe_accorpate > 1 && <span className="text-xs text-amber-700 font-semibold">({d.righe_accorpate} righe accorpate)</span>}
                  {d.crea_account && (
                    <span className={`px-1.5 py-0.5 rounded text-[11px] font-bold ${d.crea_default ? "bg-amber-100 text-amber-800 border border-amber-400" : "bg-slate-100 text-slate-600"}`}>
                      account: {d.crea_account}{d.crea_default ? " (default)" : ""}{d.account === "manca email" ? " · manca email" : ""}
                    </span>
                  )}
                  {d.categorie?.length > 0 && d.categorie.map((c) => (
                    <span key={c} className="px-1.5 py-0.5 rounded text-[11px] font-bold bg-sky-100 text-sky-800">{c}</span>
                  ))}
                  {d.warning && <span className="text-xs text-amber-700 font-semibold">⚠ {d.warning}</span>}
                  {d.dati_catastali && <span className="text-xs text-slate-500 truncate">— {d.dati_catastali}</span>}
                </div>
              ))}
            </div>
          )}
          {esito.scartate?.length > 0 && (
            <div className="text-sm text-red-700">
              <b>Righe scartate:</b> {esito.scartate.map((s) => `riga ${s.riga} (${s.motivo})`).join("; ")}
            </div>
          )}
          <div className="actions-row">
            <Button variant="outline" className="h-11" onClick={() => setStep(2)}><ArrowLeft className="w-4 h-4 mr-1" /> Modifica mappatura</Button>
            {esito.dry_run ? (
              <Button data-testid="import-esegui-btn" disabled={busy} onClick={() => run(true)}
                className="h-11 bg-green-700 hover:bg-green-800">
                <PlayCircle className="w-5 h-5 mr-2" /> {busy ? "Import in corso..." : "Conferma e importa"}
              </Button>
            ) : (
              <Button className="h-11 bg-brand hover:bg-brand-dark" onClick={() => { setStep(1); setAnalisi(null); setEsito(null); }}>
                Nuovo import
              </Button>
            )}
          </div>
        </Card>
      )}

      <Card className="p-5 border-2 border-amber-200 bg-amber-50/40" data-testid="dedup-card">
        <div className="flex items-start gap-3">
          <Users2 className="w-6 h-6 text-amber-700 shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <h3 className="font-display text-xl font-bold text-slate-800">Manutenzione dati — Unifica anagrafiche duplicate</h3>
            <p className="text-sm text-slate-600 mt-1">
              Se un import precedente ha creato più anagrafiche per lo stesso soggetto (tipico dei file Danea <b>senza Codice Fiscale</b>,
              con righe multiple per immobile/box/cantina), questa procedura le unifica in una sola conservando i dati catastali di tutte
              le proprietà (separati da virgola). Le proprietà rimangono invariate come record singoli e vengono ri-associate all'anagrafica principale.
            </p>
            <p className="text-xs text-slate-500 mt-1">
              Regole: stesso <b>CF</b> normalizzato = stessa persona; oppure stesso <b>nome</b> normalizzato (case/spazi/accenti irrilevanti) se entrambi senza CF.
              I duplicati finiscono nel <b>Cestino</b> — recuperabili in caso di errore.
            </p>
            <div className="mt-3 grid md:grid-cols-[1fr_auto_auto] gap-2 items-end">
              <div>
                <Label className="text-xs">Condominio da analizzare</Label>
                <select data-testid="dedup-condominio" value={dedupCond} onChange={(e) => { setDedupCond(e.target.value); setDedupPreview(null); }}
                        className="w-full h-11 mt-1 border rounded-md px-3 bg-white">
                  <option value="">— Seleziona —</option>
                  {condomini.map((c) => <option key={c.id} value={c.id}>{c.nome}</option>)}
                </select>
              </div>
              <Button data-testid="dedup-preview" variant="outline" className="h-11" onClick={() => runDedup(true)} disabled={!dedupCond || dedupBusy}>
                Analizza (dry-run)
              </Button>
              <Button data-testid="dedup-esegui" className="h-11 bg-brand hover:bg-brand-dark" onClick={() => runDedup(false)}
                      disabled={!dedupPreview || (dedupPreview.gruppi_duplicati === 0 && (dedupPreview.proprieta_duplicate_previste || 0) === 0) || dedupBusy}>
                {dedupBusy ? "Elaborazione..." : "Esegui unificazione"}
              </Button>
            </div>
            {dedupPreview && (
              <div className="mt-3 space-y-2" data-testid="dedup-preview-result">
                <div className={`p-2.5 rounded-lg text-sm ${dedupPreview.gruppi_duplicati > 0 || (dedupPreview.proprieta_duplicate_previste || 0) > 0 ? "bg-amber-100 text-amber-900" : "bg-emerald-100 text-emerald-900"}`}>
                  <b>{dedupPreview.condominio}</b>: {dedupPreview.residenti_totali} anagrafiche totali · {" "}
                  {dedupPreview.gruppi_duplicati > 0
                    ? <>trovati <b>{dedupPreview.gruppi_duplicati}</b> gruppi di duplicati (<b>{dedupPreview.duplicati_da_rimuovere}</b> anagrafiche da unificare)</>
                    : "nessuna anagrafica duplicata"}
                  {(dedupPreview.proprieta_duplicate_previste || 0) > 0 && (
                    <> · <b>{dedupPreview.proprieta_duplicate_previste}</b> schede RAC duplicate da unificare</>
                  )}
                </div>
                {dedupPreview.dettagli.length > 0 && (
                  <div className="max-h-64 overflow-y-auto border rounded-lg divide-y">
                    {dedupPreview.dettagli.map((d, i) => (
                      <div key={i} className="p-2.5 text-sm" data-testid={`dedup-detail-${i}`}>
                        <div className="flex items-baseline gap-2">
                          <b>{d.nome_principale}</b>
                          <span className="text-xs text-slate-500">principale</span>
                          {d.principale_ha_account && <span className="text-[10px] font-bold text-emerald-800 bg-emerald-100 px-1.5 py-0.5 rounded">CON ACCOUNT</span>}
                        </div>
                        <div className="text-xs text-slate-600 mt-0.5">
                          Duplicati da assorbire ({d.duplicati_nomi.length}): <i>{d.duplicati_nomi.join(", ")}</i>
                        </div>
                        {d.duplicati_con_account > 0 && (
                          <div className="text-xs text-amber-800 mt-0.5">
                            ⚠ {d.duplicati_con_account} duplicato/i ha account: verrà cestinato ma il suo account NON verrà toccato (rimarrà attivo, orfano)
                          </div>
                        )}
                        <div className="text-xs text-slate-500 mt-1">
                          Proprietà da ri-associare: <b>{d.proprieta_da_riassociare}</b>
                          {d.dati_catastali_finali && (<> · Dati catastali finali: <span className="italic">{d.dati_catastali_finali}</span></>)}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </Card>

      <Card className="p-5 border-2">
        <button type="button" data-testid="storico-toggle"
          className="w-full flex items-center justify-between gap-3 text-left"
          onClick={() => setStoricoOpen((v) => !v)}>
          <h3 className="font-display text-xl font-bold flex items-center gap-2">
            Storico import
            <span className="text-sm font-normal text-slate-500">({batches.length})</span>
          </h3>
          {storicoOpen ? <ChevronUp className="w-5 h-5 text-slate-500" /> : <ChevronDown className="w-5 h-5 text-slate-500" />}
        </button>
        {/* Iter122k6 · Ricostruisci lotto PDF Danea storico (pre-Iter122k5) */}
        {storicoOpen && (
          <div className="mt-3 mb-3 p-3 rounded-lg border-2 border-amber-300 bg-amber-50/60 text-sm text-amber-900" data-testid="ricostruisci-pdf-banner">
            <b>Import PDF Danea più vecchi del 6 feb 2026?</b>
            <div className="mt-1">Se hai importato un PDF Danea PRIMA del fix e non lo vedi nella lista, puoi ricostruire il lotto sintetico dai soggetti/relazioni marcati come <code>created_from=danea_pdf</code> e poi annullarlo con il normale flusso "Annulla lotto".</div>
            <div className="mt-2 flex flex-wrap gap-2 items-center">
              <select data-testid="ricostruisci-pdf-cond" value={condominioId || ""}
                onChange={(e) => setCondominioId(e.target.value)}
                className="h-9 px-2 border-2 border-slate-300 rounded-md bg-white text-sm max-w-[280px]">
                <option value="">— Scegli condominio —</option>
                {(condomini || []).map((c) => (<option key={c.id} value={c.id}>{c.nome}</option>))}
              </select>
              <Button size="sm" variant="outline" data-testid="ricostruisci-pdf-btn"
                disabled={!condominioId}
                onClick={async () => {
                  if (!condominioId) return;
                  try {
                    const preview = (await api.post("/import/danea-pdf/ricostruisci-lotto-storico",
                      { condominio_id: condominioId, dry_run: true })).data;
                    const s = preview.stats || {};
                    const msg = `Da rimuovere:\n• ${s.unita_da_rimuovere || 0} unità\n• ${s.soggetti_da_rimuovere || 0} soggetti (${s.soggetti_pdf_condivisi_saltati || 0} saltati perché condivisi con altri condomìni)\n• ${s.relazioni_da_rimuovere || 0} relazioni\n\nProcedere alla creazione del lotto sintetico? (poi lo annullerai)`;
                    if (!window.confirm(msg)) return;
                    const r = (await api.post("/import/danea-pdf/ricostruisci-lotto-storico",
                      { condominio_id: condominioId, dry_run: false })).data;
                    if (r.batch_id) {
                      toast.success(`Lotto ricostruito. Ora puoi annullarlo dalla lista.`);
                      loadBatches();
                    } else {
                      toast.info(r.note || "Nessun record da ricostruire.");
                    }
                  } catch (e) {
                    toast.error(formatApiError(e.response?.data?.detail) || "Ricostruzione lotto fallita");
                  }
                }}>
                Ricostruisci lotto PDF storico
              </Button>
            </div>
          </div>
        )}
        {storicoOpen && (batches.length === 0 ? <p className="text-sm text-slate-400 italic mt-3">Nessun import eseguito.</p> : (
          <div className="divide-y mt-3">
            {batches.map((b) => {
              const sync = b.report?.sync_new_model;
              const nuoviIds = sync && Array.isArray(sync.soggetti_creati_ids) ? sync.soggetti_creati_ids.length : 0;
              return (
                <div key={b.id} className={`py-2.5 flex flex-wrap items-center gap-3 text-sm border-l-4 pl-3 ${b.annullato ? "border-l-slate-300" : "border-l-emerald-500"}`} data-testid={`batch-${b.id}`}>
                  <FileSpreadsheet className={`w-4 h-4 shrink-0 ${b.annullato ? "text-slate-400" : "text-emerald-600"}`} />
                  <b>{b.filename}</b>
                  <span className="text-slate-500">{b.entita} · {fmt(b.created_at)} · {b.eseguito_da}</span>
                  {/* Iter122k5 · PDF Danea usa `report.stats` invece di `report.creati/aggiornati` */}
                  {b.entita === "danea_pdf" ? (
                    <span className="text-xs text-slate-600">
                      unità {b.report?.stats?.unita_create || 0}/{b.report?.stats?.unita_aggiornate || 0} ·
                      soggetti {b.report?.stats?.soggetti_creati || 0}/{b.report?.stats?.soggetti_aggiornati || 0} ·
                      relazioni {b.report?.stats?.relazioni_create || 0}
                    </span>
                  ) : (
                    <span className="text-xs text-slate-600">creati {b.report?.creati} · aggiornati {b.report?.aggiornati}</span>
                  )}
                  {nuoviIds > 0 && (
                    <span className="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 text-xs font-bold" title="Soggetti creati nel Registro Anagrafico">
                      <Sparkles className="w-3 h-3 inline mr-0.5" />{nuoviIds} nuovi
                    </span>
                  )}
                  {b.annullato ? (
                    <span className="px-2 py-0.5 rounded-full bg-slate-200 text-slate-600 text-xs font-bold" data-testid={`batch-stato-${b.id}`}>Annullato</span>
                  ) : (
                    <span className="px-2 py-0.5 rounded-full bg-emerald-600 text-white text-xs font-bold inline-flex items-center gap-1" data-testid={`batch-stato-${b.id}`}>
                      <span className="w-1.5 h-1.5 rounded-full bg-white/90 animate-pulse" /> Attivo
                    </span>
                  )}
                  {!b.annullato && (
                    <div className="ml-auto flex gap-2 flex-wrap">
                      <Button size="sm" variant="outline" data-testid={`batch-report-${b.id}`}
                        className="h-8 text-sky-800 border-sky-300" onClick={() => apriReport(b.id)}>
                        <FileText className="w-3.5 h-3.5 mr-1" /> Report
                      </Button>
                      <Button size="sm" variant="outline" data-testid={`batch-annulla-${b.id}`}
                        className="h-8 text-red-700 border-red-300" onClick={() => annulla(b.id)}>
                        <Undo2 className="w-3.5 h-3.5 mr-1" /> Annulla lotto
                      </Button>
                    </div>
                  )}
                  {b.annullato && (
                    <div className="ml-auto flex gap-2">
                      <Button size="sm" variant="outline" data-testid={`batch-report-${b.id}`}
                        className="h-8 text-sky-800 border-sky-300" onClick={() => apriReport(b.id)}>
                        <FileText className="w-3.5 h-3.5 mr-1" /> Report
                      </Button>
                      <Button size="sm" variant="outline" data-testid={`batch-elimina-${b.id}`}
                        className="h-8 text-red-700 border-red-300 hover:bg-red-50"
                        onClick={() => setDeleteTarget(b)} title="Elimina definitivamente dallo storico">
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ))}
      </Card>

      {/* Iter117 · Dialog Report lotto (backend endpoint /api/import/batches/{bid}/report) */}
      <BatchReportDialog open={reportOpen} onClose={() => setReportOpen(false)}
        data={reportData} loading={reportLoading} />

      {/* Iter122dm · Conferma eliminazione definitiva lotto annullato */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="batch-elimina-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-600" /> Eliminare definitivamente il lotto?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Stai per rimuovere in modo definitivo dallo storico il lotto <b>{deleteTarget?.filename}</b>
              {deleteTarget?.created_at ? <> del {fmt(deleteTarget.created_at)}</> : null}.
              Il lotto è già stato annullato: questa azione non ripristina né elimina ulteriori dati, ma cancella la riga dallo storico import.
              <br /><br />
              <span className="text-red-700 font-semibold">Operazione non annullabile.</span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="batch-elimina-cancel">Annulla</AlertDialogCancel>
            <AlertDialogAction data-testid="batch-elimina-conferma" disabled={deleting}
              onClick={eliminaDefinitivamente}
              className="bg-red-600 hover:bg-red-700 text-white">
              {deleting ? "Eliminazione…" : "Sì, elimina definitivamente"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Iter122dn · Conferma annulla con warning batch successivi impattati */}
      <AlertDialog open={!!annullaTarget} onOpenChange={(o) => !o && setAnnullaTarget(null)}>
        <AlertDialogContent data-testid="batch-annulla-impacted-dialog" className="max-w-xl">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-600" /> Attenzione · rischio perdita dati
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3">
                <p>
                  {annullaTarget?.impacted_by?.length === 1
                    ? "Un import successivo ha toccato gli stessi record di questo lotto:"
                    : `${annullaTarget?.impacted_by?.length || 0} import successivi hanno toccato gli stessi record di questo lotto:`}
                </p>
                <ul className="border rounded-lg divide-y bg-amber-50">
                  {(annullaTarget?.impacted_by || []).map((imp) => (
                    <li key={imp.batch_id} className="p-2 text-sm flex items-center gap-2" data-testid={`impacted-batch-${imp.batch_id}`}>
                      <FileSpreadsheet className="w-4 h-4 text-amber-700 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold truncate">{imp.filename}</div>
                        <div className="text-xs text-slate-600">{fmt(imp.created_at)} · {imp.eseguito_da} · {imp.hits_count} record impattati</div>
                      </div>
                    </li>
                  ))}
                </ul>
                <p className="text-sm">
                  Procedendo, i record del lotto corrente verranno rimossi/ripristinati. Se questi record sono stati poi modificati dagli import qui sopra, quelle modifiche <b>andranno perse</b>.
                </p>
                <p className="text-red-700 font-semibold text-sm">Operazione non annullabile. Conferma solo se sei sicuro.</p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="batch-annulla-impacted-cancel">Annulla</AlertDialogCancel>
            <AlertDialogAction data-testid="batch-annulla-impacted-conferma"
              onClick={() => annullaTarget && annulla(annullaTarget.bid, true)}
              className="bg-red-600 hover:bg-red-700 text-white">
              Procedi comunque
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

/* ============================ REPORT POST-IMPORT ============================ */
function PostImportReport({ esito, onSollecita, busy }) {
  const s = esito.sync_new_model || {};
  const nuoviIds = Array.isArray(s.soggetti_creati_ids) ? s.soggetti_creati_ids : [];
  return (
    <div className="rounded-xl border-2 border-emerald-300 bg-gradient-to-br from-emerald-50 to-white p-5 space-y-4" data-testid="post-import-report">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-full bg-emerald-600 flex items-center justify-center text-white shrink-0">
          <Sparkles className="w-5 h-5" />
        </div>
        <div className="flex-1">
          <h4 className="font-display text-xl font-bold text-emerald-900">Report post-Import — Registro Anagrafico</h4>
          <p className="text-sm text-slate-700 mt-1">
            L&apos;import è stato sincronizzato anche sul nuovo modello a unità immobiliari (Vista Nuova).
            Le nuove anagrafiche sono già in "Anagrafica Condòmini · Vista Nuova".
          </p>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard icon={<UserPlus className="w-5 h-5" />} label="Soggetti nuovi" value={s.soggetti_creati || 0} tone="emerald" testid="stat-soggetti-nuovi" />
        <StatCard icon={<Home className="w-5 h-5" />} label="Unità aggiunte" value={s.unita_create || 0} tone="brand" testid="stat-unita-nuove" />
        <StatCard icon={<Link2 className="w-5 h-5" />} label="Relazioni create" value={s.relazioni_create || 0} tone="sky" testid="stat-rel-nuove" />
        <StatCard icon={<CheckCircle2 className="w-5 h-5" />} label="Aggiornati" value={(s.soggetti_aggiornati || 0) + (s.unita_aggiornate || 0) + (s.relazioni_aggiornate || 0)} tone="slate" testid="stat-aggiornati" />
      </div>

      {nuoviIds.length > 0 ? (
        <div className="p-4 rounded-lg border-2 border-amber-300 bg-amber-50 flex flex-wrap items-center gap-3" data-testid="post-import-sollecita-cta">
          <Send className="w-6 h-6 text-amber-700 shrink-0" />
          <div className="flex-1 min-w-[220px]">
            <div className="font-bold text-amber-900">Invia il modulo RAC ai {nuoviIds.length} nuovi soggetti</div>
            <div className="text-xs text-amber-800 mt-0.5">
              Ricevono una email col link al modulo pubblico dove potranno confermare/aggiornare i propri dati (CF, IBAN, contatti, contratto locazione).
            </div>
          </div>
          <Button data-testid="post-import-sollecita-btn" className="h-11 bg-amber-600 hover:bg-amber-700 text-white"
            disabled={busy} onClick={onSollecita}>
            <Send className="w-4 h-4 mr-2" /> Sollecita RAC ai nuovi
          </Button>
        </div>
      ) : (
        <div className="text-sm text-slate-600 italic">
          Nessun soggetto nuovo creato — l&apos;import ha solo aggiornato i dati esistenti.
        </div>
      )}
    </div>
  );
}

function StatCard({ icon, label, value, tone = "emerald", testid }) {
  const toneMap = {
    emerald: "border-emerald-300 bg-emerald-50 text-emerald-800",
    brand: "border-brand/40 bg-brand/5 text-brand",
    sky: "border-sky-300 bg-sky-50 text-sky-800",
    slate: "border-slate-300 bg-white text-slate-700",
  };
  return (
    <div className={`rounded-lg border-2 py-3 px-3 ${toneMap[tone]} flex items-center gap-3`} data-testid={testid}>
      <div className="opacity-70">{icon}</div>
      <div>
        <div className="text-2xl font-bold leading-tight">{value}</div>
        <div className="text-[11px] uppercase tracking-wide font-bold opacity-80">{label}</div>
      </div>
    </div>
  );
}

/* ============================ DIFF-REPORT PRE-IMPORT ============================ */
function DiffReportPanel({ dr }) {
  const [expandNuovi, setExpandNuovi] = useState(false);
  const [expandMod, setExpandMod] = useState(false);
  const tot = (dr.soggetti_nuovi_count || 0) + (dr.soggetti_modificati_count || 0);
  return (
    <div className="rounded-xl border-2 border-sky-300 bg-gradient-to-br from-sky-50 to-white p-5 space-y-4" data-testid="diff-report-panel">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-full bg-sky-600 flex items-center justify-center text-white shrink-0">
          <GitCompare className="w-5 h-5" />
        </div>
        <div className="flex-1">
          <h4 className="font-display text-xl font-bold text-sky-900">Diff-Report — Cosa cambierà con l&apos;import</h4>
          <p className="text-sm text-slate-700 mt-1">
            Anteprima delle differenze rispetto ai dati già presenti. Nessuna modifica ancora salvata.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard icon={<UserPlus className="w-5 h-5" />} label="Soggetti nuovi" value={dr.soggetti_nuovi_count || 0} tone="emerald" testid="diff-stat-nuovi" />
        <StatCard icon={<GitCompare className="w-5 h-5" />} label="Soggetti modificati" value={dr.soggetti_modificati_count || 0} tone="brand" testid="diff-stat-modificati" />
        <StatCard icon={<Home className="w-5 h-5" />} label="Unità nuove" value={dr.unita_nuove_count || 0} tone="sky" testid="diff-stat-unita-nuove" />
        <StatCard icon={<CheckCircle2 className="w-5 h-5" />} label="Invariati" value={dr.invariati_count || 0} tone="slate" testid="diff-stat-invariati" />
      </div>

      {tot === 0 ? (
        <div className="text-sm text-slate-600 italic">
          Nessuna variazione rilevata rispetto ai dati esistenti — l&apos;import non produrrà cambiamenti.
        </div>
      ) : (
        <>
          {/* Soggetti nuovi */}
          {(dr.soggetti_nuovi || []).length > 0 && (
            <div className="border-2 border-emerald-200 rounded-lg overflow-hidden" data-testid="diff-nuovi-block">
              <button type="button" onClick={() => setExpandNuovi((s) => !s)}
                className="w-full flex items-center justify-between px-4 py-2 bg-emerald-50 hover:bg-emerald-100 font-bold text-emerald-900 text-sm"
                data-testid="diff-nuovi-toggle">
                <span className="flex items-center gap-2"><UserPlus className="w-4 h-4" /> Soggetti che verranno CREATI ({dr.soggetti_nuovi.length})</span>
                {expandNuovi ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </button>
              {expandNuovi && (
                <div className="p-3 max-h-72 overflow-y-auto">
                  <table className="w-full text-sm" data-testid="diff-nuovi-table">
                    <thead>
                      <tr className="text-left text-xs uppercase text-slate-500 border-b">
                        <th className="py-1 pr-2">Nome</th>
                        <th className="pr-2">CF</th>
                        <th className="pr-2">N. unità</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dr.soggetti_nuovi.map((s, i) => (
                        <tr key={i} className="border-b border-slate-100" data-testid={`diff-nuovo-row-${i}`}>
                          <td className="py-1.5 pr-2 font-semibold">{s.nome}</td>
                          <td className="pr-2 text-xs font-mono">{s.cf || "—"}</td>
                          <td className="pr-2">{s.unita_count}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* Soggetti modificati */}
          {(dr.soggetti_modificati || []).length > 0 && (
            <div className="border-2 border-brand/30 rounded-lg overflow-hidden" data-testid="diff-modificati-block">
              <button type="button" onClick={() => setExpandMod((s) => !s)}
                className="w-full flex items-center justify-between px-4 py-2 bg-brand/5 hover:bg-brand/10 font-bold text-brand text-sm"
                data-testid="diff-modificati-toggle">
                <span className="flex items-center gap-2"><GitCompare className="w-4 h-4" /> Soggetti che verranno MODIFICATI ({dr.soggetti_modificati.length})</span>
                {expandMod ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </button>
              {expandMod && (
                <div className="p-3 max-h-96 overflow-y-auto space-y-3">
                  {dr.soggetti_modificati.map((s, i) => (
                    <div key={i} className="p-2.5 rounded border border-slate-200 bg-white" data-testid={`diff-mod-row-${i}`}>
                      <div className="flex items-baseline gap-2 mb-1.5">
                        <span className="font-bold text-slate-900">{s.nome}</span>
                        {s.cf && <span className="text-xs font-mono text-slate-500">{s.cf}</span>}
                        <span className="ml-auto text-xs text-slate-500">{s.changes.length} campo/i</span>
                      </div>
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="text-left uppercase text-slate-400 border-b">
                            <th className="py-0.5 pr-2 w-40">Campo</th>
                            <th className="pr-2">Prima</th>
                            <th className="pr-2">Dopo</th>
                          </tr>
                        </thead>
                        <tbody>
                          {s.changes.map((c, j) => (
                            <tr key={j} className="border-b border-slate-50 last:border-b-0">
                              <td className="py-1 pr-2 font-semibold text-slate-700">{c.campo}</td>
                              <td className="pr-2 text-slate-500 line-through break-all">{c.prima || <i>vuoto</i>}</td>
                              <td className="pr-2 text-emerald-800 font-semibold break-all">{c.dopo || <i>vuoto</i>}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="text-xs text-slate-600 italic">
            💡 Puoi ora cliccare <b>&laquo;Esegui import&raquo;</b> per confermare, oppure <b>&laquo;Annulla&raquo;</b> per correggere il file e riprovare.
          </div>
        </>
      )}
    </div>
  );
}


/* ============================ REPORT LOTTO (STORICO) ============================ */
function BatchReportDialog({ open, onClose, data, loading }) {
  const sync = data?.report?.sync_new_model || {};
  const soggConEmail = data?.soggetti_nuovi_con_email ?? 0;
  const soggSenzaEmail = data?.soggetti_nuovi_senza_email ?? 0;
  const soggetti = data?.soggetti_nuovi || [];
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent data-testid="batch-report-dialog" className="max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl flex items-center gap-2">
            <FileText className="w-6 h-6 text-sky-600" /> Report lotto di import
          </DialogTitle>
        </DialogHeader>
        {loading ? (
          <div className="py-8 text-center text-slate-500 text-sm">Caricamento report...</div>
        ) : !data ? null : (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
              <div><b className="text-slate-600">File:</b> {data.filename}</div>
              <div><b className="text-slate-600">Data:</b> {fmt(data.created_at)}</div>
              <div><b className="text-slate-600">Eseguito da:</b> {data.eseguito_da}</div>
              <div><b className="text-slate-600">Stato:</b> {data.annullato ? <span className="text-red-700 font-bold">Annullato</span> : <span className="text-emerald-700 font-bold">Attivo</span>}</div>
            </div>
            <div className="rounded-lg border-2 border-slate-200 p-3">
              <h4 className="font-bold text-slate-800 mb-2 text-base">Contatori import legacy</h4>
              <div className="grid grid-cols-3 gap-3 text-sm">
                <div className="bg-emerald-50 rounded p-2"><b className="text-emerald-800">Creati:</b> {data.report?.creati ?? 0}</div>
                <div className="bg-amber-50 rounded p-2"><b className="text-amber-800">Aggiornati:</b> {data.report?.aggiornati ?? 0}</div>
                <div className="bg-slate-100 rounded p-2"><b className="text-slate-700">Invariati:</b> {data.report?.invariati ?? 0}</div>
              </div>
            </div>
            {sync && Object.keys(sync).length > 0 && (
              <div className="rounded-lg border-2 border-emerald-200 bg-emerald-50 p-3">
                <h4 className="font-bold text-emerald-900 mb-2 text-base flex items-center gap-1">
                  <Sparkles className="w-4 h-4" /> Sync Registro Anagrafico
                </h4>
                {sync.error ? (
                  <div className="text-sm text-red-700"><b>Errore:</b> {sync.error}</div>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
                    <div><b>Soggetti:</b> {sync.soggetti_creati ?? 0}</div>
                    <div><b>Unità:</b> {sync.unita_create ?? 0}</div>
                    <div><b>Relazioni:</b> {sync.relazioni_create ?? 0}</div>
                    <div><b>Referenti:</b> {sync.referenti_impostati ?? 0}</div>
                  </div>
                )}
              </div>
            )}
            <div className="rounded-lg border-2 border-sky-200 p-3">
              <h4 className="font-bold text-sky-900 mb-2 text-base flex items-center gap-2">
                <KeyRound className="w-4 h-4" /> Account e credenziali
              </h4>
              {/* Iter120f · contatori reali degli account per i residenti legacy del lotto */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-sm mb-2">
                <div className="bg-emerald-50 rounded p-2" data-testid="report-account-creati">
                  <b className="text-emerald-800">Account creati:</b> {data.report?.account_creati ?? 0}
                </div>
                <div className="bg-sky-50 rounded p-2" data-testid="report-account-collegati">
                  <b className="text-sky-800">Account collegati:</b> {data.report?.account_collegati ?? 0}
                </div>
                <div className={`rounded p-2 ${(data.report?.account_falliti?.length || 0) > 0 ? "bg-red-50" : "bg-slate-100"}`} data-testid="report-account-falliti">
                  <b className={(data.report?.account_falliti?.length || 0) > 0 ? "text-red-800" : "text-slate-700"}>Falliti:</b> {data.report?.account_falliti?.length ?? 0}
                </div>
              </div>
              {(data.report?.account_falliti?.length || 0) > 0 && (
                <details className="text-xs bg-red-50 border border-red-200 rounded p-2 mb-2" data-testid="report-account-falliti-list">
                  <summary className="cursor-pointer text-red-800 font-semibold">Dettaglio {data.report.account_falliti.length} account non creati</summary>
                  <div className="mt-2 space-y-1 max-h-48 overflow-y-auto">
                    {data.report.account_falliti.map((f, i) => (
                      <div key={i} className="border-b border-red-100 pb-1">
                        <div className="font-semibold text-red-900">{f.nome}</div>
                        <div className="text-slate-700">{f.email} — <span className="italic">{f.motivo}</span></div>
                      </div>
                    ))}
                  </div>
                </details>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                <div className="bg-emerald-50 rounded p-2 flex items-center gap-2">
                  <Mail className="w-4 h-4 text-emerald-700" />
                  <span><b>{soggConEmail}</b> nuovi soggetti con email (Registro Anagrafico)</span>
                </div>
                <div className="bg-amber-50 rounded p-2 flex items-center gap-2">
                  <MailX className="w-4 h-4 text-amber-700" />
                  <span><b>{soggSenzaEmail}</b> nuovi soggetti senza email</span>
                </div>
              </div>
              <p className="text-xs text-slate-500 mt-2">
                In caso di <b>annullamento del lotto</b>: gli account CREATI in questo import verranno eliminati; quelli COLLEGATI (già esistenti su altri condomini) verranno solo scollegati da questo condominio.
              </p>
            </div>
            {soggetti.length > 0 && (
              <div className="rounded-lg border-2 border-slate-200 p-3">
                <h4 className="font-bold text-slate-800 mb-2 text-base">Soggetti creati ({data.soggetti_nuovi_totali ?? soggetti.length})</h4>
                <div className="max-h-56 overflow-y-auto divide-y text-sm">
                  {soggetti.map((s) => (
                    <div key={s.id} className="py-1.5 flex items-center gap-2" data-testid={`report-soggetto-${s.id}`}>
                      {s.email ? <Mail className="w-3.5 h-3.5 text-emerald-600 shrink-0" /> : <MailX className="w-3.5 h-3.5 text-amber-600 shrink-0" />}
                      <b>{s.nome}</b>
                      {s.codice_fiscale && <span className="text-slate-500 text-xs">· CF {s.codice_fiscale}</span>}
                      {s.email && <span className="text-slate-500 text-xs ml-auto">{s.email}</span>}
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div className="flex justify-end pt-2">
              <Button variant="outline" onClick={onClose} data-testid="batch-report-close">Chiudi</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
